import { whereField } from './fields/index.js';
import { OPERATOR_LABEL, type WhereOperator } from './operators.js';
import {
  type WhereDataset,
  type WhereField,
  type WhereGroup,
  type WhereRule,
  isWhereGroup,
} from './types.js';

export interface DescribeLabels {
  /** Names for values a field list cannot know (View names, tier names), by field key. */
  options?: Record<string, Record<string, string>>;
}

const RELATION_NOUN = {
  project: 'Project',
  firm: 'Firm',
  contact: 'Contact',
  signal: 'Signal',
} as const;

/** "City", or for a related field the linked record's noun first: "Project city", "Firm ICP Score". */
export function whereFieldName(dataset: WhereDataset, key: string): string {
  const field = whereField(dataset, key);
  if (!field) return key;
  if (!field.relation) return field.label;
  const label = /^[A-Z][a-z]/.test(field.label)
    ? `${field.label[0]?.toLowerCase()}${field.label.slice(1)}`
    : field.label;
  return `${RELATION_NOUN[field.relation]} ${label}`;
}

function numberText(field: WhereField | undefined, value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '');
  if (field?.unit === 'year') return String(n);
  const text = n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return field?.unit === 'usd' ? `$${text}` : text;
}

/** The value part of a rule in plain words: "Boston or Cambridge", "50 and 200", "3 months ago", "Yes". */
export function whereValueText(
  dataset: WhereDataset,
  rule: WhereRule,
  labels: DescribeLabels = {},
): string {
  const field = whereField(dataset, rule.field);
  const named = labels.options?.[rule.field] ?? {};
  const optionLabel = (v: string) =>
    named[v] ?? field?.options?.find((o) => o.value === v)?.label ?? v;
  const v = rule.value;
  if (Array.isArray(v) && rule.operator === 'between') {
    return field?.kind === 'date'
      ? `${v[0]} and ${v[1]}`
      : `${numberText(field, v[0])} and ${numberText(field, v[1])}`;
  }
  if (Array.isArray(v)) return v.map((x) => optionLabel(String(x))).join(' or ');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return numberText(field, v);
  if (v && typeof v === 'object') {
    const { amount, unit } = v as { amount: number; unit: string };
    return `${amount} ${amount === 1 ? unit.replace(/s$/, '') : unit} ago`;
  }
  return String(v ?? '');
}

export function describeRule(
  dataset: WhereDataset,
  rule: WhereRule,
  labels: DescribeLabels = {},
): string {
  const op = OPERATOR_LABEL[rule.operator as WhereOperator] ?? rule.operator;
  const name = whereFieldName(dataset, rule.field);
  if (rule.operator === 'null' || rule.operator === 'notNull') return `${name} ${op}`;
  return `${name} ${op} ${whereValueText(dataset, rule, labels)}`;
}

export function describeWhere(
  dataset: WhereDataset,
  where: WhereGroup,
  labels: DescribeLabels = {},
  nested = false,
): string {
  const parts = where.rules.map((node) =>
    isWhereGroup(node)
      ? describeWhere(dataset, node, labels, true)
      : describeRule(dataset, node, labels),
  );
  const joined = parts.join(where.combinator === 'or' ? ' or ' : ' and ');
  const body = nested && parts.length > 1 ? `(${joined})` : joined;
  return where.not ? `not ${parts.length > 1 && !nested ? `(${joined})` : body}` : body;
}
